import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, TextField, Button } from "@mui/material";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Sort,
  Filter,
} from "@syncfusion/ej2-react-grids";
import SideBar from "../sideBar/SideBar";
import { useNavigate } from "react-router-dom";

export default function RunningHoursEditor() {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const todayStr = useMemo(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  }, []);

  const [dayDate, setDayDate] = useState(todayStr);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // بحث بالاسم والكود + مراجع للمدخلات
  const [runnerNameQuery, setRunnerNameQuery] = useState("");
  const [runnerCodeQuery, setRunnerCodeQuery] = useState("");
  const [runnerBranchQuery, setRunnerBranchQuery] = useState("");
  const nameInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const debounceRef = useRef({ name: 0, code: 0, branch: 0 });

  // نخزن القيم المعدلة هنا لتفادي فقدان الفوكس
  // الشكل: { [serial]: { runningHours?: "0-24", assignedOrders?: "0-9999" } }
  const editedRef = useRef({});

  const accessToken = localStorage.getItem("userStorage")
    ? JSON.parse(localStorage.getItem("userStorage")).accessToken
    : null;

  const headers = {
    CompanyID: "241701",
    AccessToken: `${accessToken}`,
    Language: "ar",
  };

  const handleFetch = async () => {
    try {
      setLoading(true);
      editedRef.current = {}; // امسح أي إدخالات قديمة
      const url = `https://anydev.com-eg.net/api/RunnersActivity/V1/GetRunningHours/${dayDate}`;
      const { data } = await axios.get(url, { headers });
      const list = Array.isArray(data?.data) ? data.data : [];
      const withDate = list.map((row) => ({ ...row, selectedDate: dayDate }));
      setRows(list);
      setRows(withDate);
      // امسح الفلاتر والقيم في حقول البحث عند تغيير التاريخ/العرض
      if (gridRef.current?.clearFiltering) gridRef.current.clearFiltering();
      setRunnerNameQuery("");
      setRunnerCodeQuery("");
      setRunnerBranchQuery("");
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ تعديل ساعات اليوم: نخزنها تحت runningHours ونحافظ على أي قيم أخرى لنفس السريال
  const handleHoursChange = (serial, e) => {
    let raw = e.target.value.replace(/[^\d]/g, "");
    const prev = editedRef.current[serial] || {};
    if (raw === "") {
      // لو فضّيت الحقل نمسح runningHours فقط
      if ("runningHours" in prev) delete prev.runningHours;
      if (Object.keys(prev).length === 0) {
        delete editedRef.current[serial];
      } else {
        editedRef.current[serial] = prev;
      }
      return;
    }
    let n = parseInt(raw, 10);
    if (isNaN(n)) n = 0;
    if (n < 0) n = 0;
    if (n > 24) n = 24;
    e.target.value = String(n); // تصحيح العرض فورًا
    editedRef.current[serial] = { ...prev, runningHours: String(n) };
  };

  // عدد الأوردرات (نفس فكرة الساعات)
  const handleOrdersChange = (serial, e) => {
    let raw = e.target.value.replace(/[^\d]/g, "");
    const prev = editedRef.current[serial] || {};
    if (raw === "") {
      if ("assignedOrders" in prev) delete prev.assignedOrders;
      if (Object.keys(prev).length === 0) delete editedRef.current[serial];
      else editedRef.current[serial] = prev;
      return;
    }
    let n = parseInt(raw, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (n > 9999) n = 9999;
    e.target.value = String(n);
    editedRef.current[serial] = { ...prev, assignedOrders: String(n) };
  };

  const handleSave = async () => {
    // ✅ نحول القيم إلى Payload بحسب الموجود لكل سريال
    const payload = Object.entries(editedRef.current).map(([serial, obj]) => ({
      serial: Number(serial),
      ...(obj.runningHours !== undefined
        ? { runningHours: Number(obj.runningHours) }
        : {}),
      ...(obj.assignedOrders !== undefined
        ? { assignedOrders: Number(obj.assignedOrders) }
        : {}),
    }));

    if (payload.length === 0) {
      alert("لا توجد بيانات لحفظها.");
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        "https://anydev.com-eg.net/api/RunnersActivity/V1/UpdateRunningHours",
        payload,
        { headers }
      );
      alert("تم الحفظ بنجاح.");
      await handleFetch();
    } catch (err) {
      console.error(err);
      alert("فشل الحفظ، برجاء المحاولة مرة أخرى.");
      if (err?.response?.status === 401) {
        localStorage.clear();
        navigate("/");
      }
    } finally {
      setSaving(false);
    }
  };

  // فلترة بالاسم (Debounce)
  const handleRunnerNameFilter = (value) => {
    setRunnerNameQuery(value);
    clearTimeout(debounceRef.current.name);
    debounceRef.current.name = setTimeout(() => {
      const grid = gridRef.current;
      if (!grid) return;
      if (value && value.trim() !== "") {
        grid.filterByColumn("runnerName", "contains", value.trim());
      } else {
        grid.removeFilteredColsByField?.("runnerName");
      }
    }, 250);
  };

  // فلترة بالكود (Debounce)
  const handleRunnerCodeFilter = (value) => {
    setRunnerCodeQuery(value);
    clearTimeout(debounceRef.current.code);
    debounceRef.current.code = setTimeout(() => {
      const grid = gridRef.current;
      if (!grid) return;
      if (value && value.trim() !== "") {
        grid.filterByColumn("runnerCode", "contains", value.trim());
      } else {
        grid.removeFilteredColsByField?.("runnerCode");
      }
    }, 250);
  };

  // فلترة بالفرع (Debounce)
  const handleRunnerBranchFilter = (value) => {
    setRunnerBranchQuery(value);
    clearTimeout(debounceRef.current.branch);
    debounceRef.current.branch = setTimeout(() => {
      const grid = gridRef.current;
      if (!grid) return;
      if (value && value.trim() !== "") {
        grid.filterByColumn("branchNameA", "contains", value.trim());
      } else {
        grid.removeFilteredColsByField?.("branchNameA");
      }
    }, 250);
  };

  // أعمدة الجدول
  const columns = (
    <ColumnsDirective>
      <ColumnDirective
        field="serial"
        headerText="السريال"
        width="120"
        textAlign="Center"
      />
      <ColumnDirective
        field="runnerCode"
        headerText="كود المندوب"
        width="120"
        textAlign="Center"
      />
      <ColumnDirective
        field="runnerName"
        headerText="اسم المندوب"
        width="180"
        textAlign="Center"
      />
      <ColumnDirective
        field="branchNameA"
        headerText="اسم الفرع"
        width="180"
        textAlign="Center"
      />
      <ColumnDirective
        field="runningHours"
        headerText="الساعات الحالية"
        width="140"
        textAlign="Center"
        template={(props) => (
          // ملاحظة: العرض هنا من الداتا. المدخل أدناه لليوم الحالي.
          <span>{props.runningHours ?? 0}</span>
        )}
      />
      <ColumnDirective
        field="assignedOrders"
        headerText="الأوردرات الحالية"
        width="140"
        textAlign="Center"
        template={(props) => (
          // ملاحظة: العرض هنا من الداتا. المدخل أدناه لليوم الحالي.
          <span>{props.assignedOrders ?? 0}</span>
        )}
      />
      <ColumnDirective
        field="selectedDate"
        headerText="التاريخ المختار"
        width="140"
        textAlign="Center"
      />
      <ColumnDirective
        headerText="ساعات اليوم (0–24)"
        width="180"
        textAlign="Center"
        template={(props) => {
          const serial = props.serial;
          return (
            <input
              style={{ width: "120px", textAlign: "center" }}
              type="number"
              inputMode="numeric"
              min={0}
              max={24}
              step={1}
              placeholder="0"
              onChange={(e) => handleHoursChange(serial, e)}
              onWheel={(e) => e.currentTarget.blur()} // منع تغيير الرقم بالسكرول
            />
          );
        }}
      />
      <ColumnDirective
        headerText="عدد الأوردرات"
        width="180"
        textAlign="Center"
        template={(props) => {
          const serial = props.serial;
          return (
            <input
              style={{ width: "120px", textAlign: "center" }}
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              placeholder="0"
              onChange={(e) => handleOrdersChange(serial, e)}
              onWheel={(e) => e.currentTarget.blur()}
            />
          );
        }}
      />
    </ColumnsDirective>
  );

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="control-pane" style={{ direction: "rtl" }}>
      <SideBar />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2, pt: 2 }}>
        <TextField
          label="تاريخ اليوم"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dayDate}
          onChange={(e) => setDayDate(e.target.value)}
          sx={{ width: 220 }}
        />
        <Button variant="contained" onClick={handleFetch} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "عرض"}
        </Button>

        <TextField
          label="بحث باسم المندوب"
          value={runnerNameQuery}
          onChange={(e) => handleRunnerNameFilter(e.target.value)}
          sx={{ width: 220 }}
          inputRef={nameInputRef}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
        />
        <TextField
          label="بحث بكود المندوب"
          value={runnerCodeQuery}
          onChange={(e) => handleRunnerCodeFilter(e.target.value)}
          sx={{ width: 220 }}
          inputRef={codeInputRef}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
        />
        <TextField
          label="بحث بالفرع"
          value={runnerBranchQuery}
          onChange={(e) => handleRunnerBranchFilter(e.target.value)}
          sx={{ width: 220 }}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
        />

        <Box sx={{ flex: 1 }} />
        <Button
          color="success"
          variant="contained"
          onClick={handleSave}
          disabled={saving || rows.length === 0}
        >
          {saving ? <CircularProgress size={20} /> : "حفظ"}
        </Button>
      </Box>

      <Box sx={{ height: 520, width: "98%", m: "0 auto", mt: 2 }}>
        <GridComponent
          ref={gridRef}
          dataSource={rows}
          allowSorting
          allowPaging
          pageSettings={{ pageSize: 20 }}
          enableStickyHeader
          allowFiltering
          filterSettings={{ type: "Excel" }}
          allowKeyboard={false}
        >
          {columns}
          <Inject services={[Page, Sort, Filter]} />
        </GridComponent>
      </Box>
    </div>
  );
}
